import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { Portal } from './Portal'
import styles from './InfoSheet.module.css'

type InfoSheetProps = {
  title:    string
  onClose:  () => void
  children: React.ReactNode
}

/**
 * Reusable rules/info sheet shell: bottom-sheet on mobile, centered dialog on
 * desktop. Esc / backdrop-click / X to close; restores focus to the opener.
 * Extracted from the original ScoringRulesSheet so every "i" surface shares it.
 */
export function InfoSheet({ title, onClose, children }: InfoSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement | null
    sheetRef.current?.focus()
    return () => {
      previousFocusRef.current?.focus()
    }
  }, [])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    <Portal>
      <div
        className={styles.backdrop}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose()
        }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div
          className={styles.sheet}
          ref={sheetRef}
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
            <button className={styles.closeBtn} onClick={onClose} aria-label="Fechar">
              <X size={18} />
            </button>
          </div>

          <div className={styles.body}>{children}</div>
        </div>
      </div>
    </Portal>
  )
}

/* ── Content parts ──────────────────────────────────────────────────────────
   Rules sheets used to reach into InfoSheet.module.css directly, which broke
   the rule that a component owns its own stylesheet. These expose the same
   shapes as components so no feature has to import this module. */

type PartProps = { children: React.ReactNode }

function InfoSheetSection({ children }: PartProps) {
  return <div className={styles.section}>{children}</div>
}

function InfoSheetSectionTitle({ children }: PartProps) {
  return <h3 className={styles.sectionTitle}>{children}</h3>
}

function InfoSheetLead({ children }: PartProps) {
  return <p className={styles.lead}>{children}</p>
}

function InfoSheetList({ children }: PartProps) {
  return <ul className={styles.list}>{children}</ul>
}

function InfoSheetItem({ children }: PartProps) {
  return <li className={styles.item}>{children}</li>
}

/** Footnote block. Renders its own ℹ marker — pass plain `<p>` children. */
function InfoSheetNote({ children }: PartProps) {
  return <div className={styles.note}>{children}</div>
}

function InfoSheetChips({ children }: PartProps) {
  return <div className={styles.chips}>{children}</div>
}

function InfoSheetChip({ children }: PartProps) {
  return <span className={styles.chip}>{children}</span>
}

function InfoSheetTable({ children }: PartProps) {
  return <table className={styles.table}>{children}</table>
}

InfoSheet.Section = InfoSheetSection
InfoSheet.SectionTitle = InfoSheetSectionTitle
InfoSheet.Lead = InfoSheetLead
InfoSheet.List = InfoSheetList
InfoSheet.Item = InfoSheetItem
InfoSheet.Note = InfoSheetNote
InfoSheet.Chips = InfoSheetChips
InfoSheet.Chip = InfoSheetChip
InfoSheet.Table = InfoSheetTable
